
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useNotifications } from '@/contexts/NotificationsContext';

export type ItineraryRequest = {
  id: string;
  name: string;
  email: string;
  phone?: string;
  trip_type?: string;
  destination: string;
  travel_dates?: string;
  budget?: string;
  message: string;
  status: string;
  created_at: string;
  updated_at: string;
};

export function useItineraryRequests() {
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<ItineraryRequest | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedRequests, setSelectedRequests] = useState<string[]>([]);
  const [retryCount, setRetryCount] = useState(0);
  const [manualRefreshing, setManualRefreshing] = useState(false);
  const queryClient = useQueryClient();
  const { refreshNotifications } = useNotifications();

  const { data: requests, isLoading, error, isError } = useQuery({
    queryKey: ['itinerary-requests', selectedStatus, retryCount],
    queryFn: async () => {
      try {
        console.log('Fetching itinerary requests');
        
        let query = supabase
          .from('itinerary_requests')
          .select('id, name, email, phone, trip_type, destination, travel_dates, budget, message, status, created_at, updated_at')
          .order('created_at', { ascending: false });
        
        if (selectedStatus) {
          query = query.eq('status', selectedStatus);
        }
        
        const { data, error } = await query;
        
        if (error) {
          console.error('Supabase error:', error);
          throw new Error(`Error fetching itinerary requests: ${error.message}`);
        }
        
        console.log('Fetched requests:', data?.length || 0);
        return data as ItineraryRequest[];
      } catch (err) {
        console.error('Fetch error:', err);
        throw new Error(`Failed to load itinerary requests: ${err instanceof Error ? err.message : 'Unknown error'}`);
      } finally {
        if (manualRefreshing) {
          setManualRefreshing(false);
          toast.success('Data refreshed');
        }
      }
    },
    enabled: true,
    retry: 1,
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { data, error } = await supabase
        .from('itinerary_requests')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select();
      
      if (error) {
        throw new Error(`Error updating status: ${error.message}`);
      }
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['itinerary-requests'] });
      refreshNotifications();
      toast.success('Status updated successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update status');
    }
  });

  const handleViewDetails = (request: ItineraryRequest) => {
    setSelectedRequest(request);
    setIsDetailModalOpen(true);
    
    if (request.status === 'new') {
      updateStatusMutation.mutate({ id: request.id, status: 'in-progress' });
    }
  };

  const handleUpdateStatus = (id: string, status: string) => {
    updateStatusMutation.mutate({ id, status });
  };

  const toggleReadStatus = (request: ItineraryRequest) => {
    const newStatus = request.status === 'new' ? 'in-progress' : 'new';
    updateStatusMutation.mutate({ id: request.id, status: newStatus });
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setManualRefreshing(true);
    toast.info('Refreshing data...');
    queryClient.invalidateQueries({ queryKey: ['itinerary-requests'] });
    refreshNotifications();
  };

  const toggleRequestSelection = (id: string) => {
    setSelectedRequests(prev => 
      prev.includes(id) 
        ? prev.filter(requestId => requestId !== id) 
        : [...prev, id]
    );
  };

  const isSelected = (id: string) => selectedRequests.includes(id);

  return {
    requests,
    isLoading,
    error,
    isError,
    selectedStatus,
    setSelectedStatus,
    selectedRequest,
    setSelectedRequest,
    isDetailModalOpen,
    setIsDetailModalOpen,
    selectedRequests,
    setSelectedRequests,
    manualRefreshing,
    handleViewDetails,
    handleUpdateStatus,
    toggleReadStatus,
    handleRetry,
    toggleRequestSelection,
    isSelected
  };
}
